/**
 * Chart utility functions for Activity Detail page
 * Handles data transformation, synchronization, and zone calculations
 */

/**
 * Chart utility functions for Activity Detail page
 * Handles data transformation, synchronization, and zone calculations
 */
import type {
  ActivityStreamResponse,
  MetricType,
  StackedChartDataPoint,
  ZoneDistribution,
  ZoneDistributionData,
  HeartRateZone,
  PaceZone,
} from '../types';

/**
 * Metric color palette for consistent visualization
 */
const METRIC_COLORS: Record<MetricType, string> = {
  pace: '#3b82f6', // blue
  gradeAdjustedPace: '#00bcd4', // cyan
  heartRate: '#ef4444', // red
  altitude: '#10b981', // green
  cadence: '#f59e0b', // amber
  power: '#8b5cf6', // purple
  speed: '#06b6d4', // cyan
};

/**
 * Get consistent color for a metric type
 */
export function getMetricColor(metricType: MetricType): string {
  return METRIC_COLORS[metricType];
}

/**
 * Format time in seconds to HH:MM:SS or MM:SS
 */
export function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format distance in meters to km with 2 decimal places
 */
export function formatDistance(meters: number): string {
  return `${(meters / 1000).toFixed(2)} km`;
}

/**
 * Sample data points for performance optimization
 * Reduces data points while maintaining visual accuracy
 */
export function sampleDataForPerformance<T extends { time: number }>(
  data: T[],
  maxPoints: number = 1000
): T[] {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  if (data.length <= maxPoints) {
    return data;
  }

  const sampledData: T[] = [];
  const step = data.length / maxPoints;

  for (let i = 0; i < maxPoints; i++) {
    const index = Math.floor(i * step);
    sampledData.push(data[index]);
  }

  // Always include the last point
  if (sampledData[sampledData.length - 1] !== data[data.length - 1]) {
    sampledData.push(data[data.length - 1]);
  }

  return sampledData;
}

/**
 * Transform activity stream data to chart-ready format for a specific metric
 */
export function transformActivityDataToChartFormat(
  streams: ActivityStreamResponse | null,
  metricType: MetricType,
  sportType?: string
): StackedChartDataPoint[] {
  if (!streams || !streams.time || streams.time.length === 0) {
    return [];
  }

  const dataKey = getDataKeyForMetric(metricType);
  const metricData = streams[dataKey];

  if (!metricData || !Array.isArray(metricData)) {
    return [];
  }

  const chartData: StackedChartDataPoint[] = [];

  // Determine fallback value for missing/null data
  // For pace, we want the "slowest" value (highest min/km) instead of 0
  let fallbackValue = 0;

  if ((metricType === 'pace' || metricType === 'gradeAdjustedPace') && streams.velocity_smooth) {
    // Find min positive speed (approx 0.1 m/s threshold to avoid noise)
    const validSpeeds = streams.velocity_smooth.filter(v => v != null && v > 0.1);
    if (validSpeeds.length > 0) {
      const minSpeed = Math.min(...validSpeeds);
      fallbackValue = convertSpeedToPace(minSpeed);
    } else {
      fallbackValue = 20; // Default slow pace (20 min/km) if no movement
    }
  }

  const isRun = sportType?.toLowerCase().includes('run');

  for (let i = 0; i < streams.time.length; i++) {
    const value = metricData[i];

    // Default to fallback
    let processedValue: number = fallbackValue;

    if (value != null) {
      if (metricType === 'pace' || metricType === 'gradeAdjustedPace') {
        const speed = value as number;
        // If speed is valid and moving, convert it. Otherwise keep fallback.
        if (speed > 0) {
          processedValue = convertSpeedToPace(speed);
        }
      } else if (metricType === 'cadence' && isRun) {
        // For runs, multiply cadence by 2 to get steps per minute (Strava convention)
        processedValue = (value as number) * 2;
      } else {
        processedValue = value as number;
      }
    }

    chartData.push({
      time: streams.time[i],
      distance: streams.distance?.[i] || 0,
      value: processedValue,
      formattedTime: formatTime(streams.time[i]),
      formattedDistance: formatDistance(streams.distance?.[i] || 0),
    });
  }

  return chartData;
}

/**
 * Get the appropriate data key from ActivityStreamResponse for a metric type
 */
function getDataKeyForMetric(metricType: MetricType): keyof ActivityStreamResponse {
  const mapping: Record<MetricType, keyof ActivityStreamResponse> = {
    pace: 'velocity_smooth',
    gradeAdjustedPace: 'grade_adjusted_speed',
    heartRate: 'heartrate',
    altitude: 'altitude',
    cadence: 'cadence',
    power: 'watts',
    speed: 'velocity_smooth',
  };
  return mapping[metricType];
}

/**
 * Convert speed (m/s) to pace (min/km)
 */
function convertSpeedToPace(speedMs: number): number {
  if (speedMs <= 0) return 0;
  const paceMinPerKm = 1000 / (speedMs * 60);
  return paceMinPerKm;
}

/**
 * Calculate shared X-axis domain across multiple metrics
 */
export function synchronizeChartDomains(
  chartDataSets: StackedChartDataPoint[][],
  xAxisType: 'time' | 'distance' = 'time'
): [number, number] {
  if (chartDataSets.length === 0 || chartDataSets.every(set => set.length === 0)) {
    return [0, 0];
  }

  const key = xAxisType === 'time' ? 'time' : 'distance';

  let min = Infinity;
  let max = -Infinity;

  chartDataSets.forEach(dataSet => {
    dataSet.forEach(point => {
      const value = point[key];
      if (value < min) min = value;
      if (value > max) max = value;
    });
  });

  // Add 2% padding on each side
  const padding = (max - min) * 0.02;
  return [Math.max(0, min - padding), max + padding];
}

/**
 * Calculate zone distribution from activity stream data
 */
export function aggregateZoneData(
  streams: ActivityStreamResponse | null,
  zones: HeartRateZone[] | PaceZone[],
  type: 'heartRate' | 'pace' | 'power'
): ZoneDistributionData {
  const zoneDistributions: ZoneDistribution[] = zones.map((zone, index) => ({
    zone: zone.zone,
    name: zone.name,
    minValue: 'min_bpm' in zone ? zone.min_bpm : zone.min_pace,
    maxValue: 'max_bpm' in zone ? zone.max_bpm : zone.max_pace,
    timeInZone: 0,
    percentageInZone: 0,
    color: getZoneColor(index),
  }));

  if (!streams || !streams.time || streams.time.length === 0) {
    return {
      type,
      zones: zoneDistributions,
      totalTime: 0,
    };
  }

  const dataKey = type === 'heartRate' ? 'heartrate' :
    type === 'pace' ? 'velocity_smooth' :
      'watts';

  const metricData = streams[dataKey];

  if (!metricData || !Array.isArray(metricData)) {
    return {
      type,
      zones: zoneDistributions,
      totalTime: 0,
    };
  }

  // Calculate time in each zone
  let totalTime = 0;

  for (let i = 0; i < streams.time.length - 1; i++) {
    const value = metricData[i];
    const timeDelta = streams.time[i + 1] - streams.time[i];

    if (value == null || timeDelta <= 0) {
      continue;
    }

    // Convert pace if needed
    const processedValue = type === 'pace'
      ? convertSpeedToPace(value as number)
      : value;

    totalTime += timeDelta;

    // Find which zone this value belongs to
    const zoneIndex = zoneDistributions.findIndex(
      zone => processedValue >= zone.minValue && processedValue <= zone.maxValue
    );

    if (zoneIndex !== -1) {
      zoneDistributions[zoneIndex].timeInZone += timeDelta;
    }
  }

  // Calculate percentages
  zoneDistributions.forEach(zone => {
    zone.percentageInZone = totalTime > 0
      ? (zone.timeInZone / totalTime) * 100
      : 0;
  });

  return {
    type,
    zones: zoneDistributions,
    totalTime,
  };
}

/**
 * Get color for zone by index
 */
export function getZoneColor(zoneIndex: number): string {
  const colors = [
    '#94a3b8', // Zone 1 - gray
    '#3b82f6', // Zone 2 - blue
    '#10b981', // Zone 3 - green
    '#f59e0b', // Zone 4 - amber
    '#ef4444', // Zone 5 - red
  ];
  return colors[zoneIndex % colors.length];
}

/**
 * Check if metric data is available in activity streams
 */
export function isMetricAvailable(
  streams: ActivityStreamResponse | null,
  metricType: MetricType
): boolean {
  if (!streams) return false;

  const dataKey = getDataKeyForMetric(metricType);
  const data = streams[dataKey];

  return Array.isArray(data) && data.length > 0 && data.some(v => v != null);
}

/**
 * Get Y-axis domain for a metric with padding
 */
export function getMetricDomain(data: StackedChartDataPoint[]): [number, number] {
  if (data.length === 0) {
    return [0, 100];
  }

  const values = data
    .map(d => d.value)
    .filter((v) => !isNaN(v));

  if (values.length === 0) {
    return [0, 100];
  }

  const min = Math.min(...values);
  const max = Math.max(...values);

  // Add 10% padding on each side
  const padding = (max - min) * 0.1;

  return [
    Math.max(0, min - padding),
    max + padding
  ];
}
